import { jsxs, jsx } from 'react/jsx-runtime';
import WebMap from '@arcgis/core/WebMap';
import { createContext, memo, useMemo, useRef, useId, useContext, useEffect } from 'react';
import { useEventHandlers } from '../../hooks/useEventHandlers';
import { ArcViewContext } from '../ArcView/ArcViewContext';
import { ArcReactiveProp } from './ArcReactiveProp';
import { isEqual } from './isEqual';

const MapContext = createContext(
  void 0
);
function createViewComponent(ViewConstructor) {
  const ArcView = ({
    children,
    onViewCreated,
    style,
    className,
    map,
    eventHandlers,
    ...mapViewProps
  }) => {
    const mapView = useMemo(
      () => new ViewConstructor({
        map: map && "constructed" in map && map.constructed ? map : new WebMap(map ?? { basemap: "arcgis-dark-gray" }),
        ...mapViewProps
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );
    const mapContainer = useRef(null);
    const internalId = useId();
    const id = mapViewProps.id ?? internalId;
    const mountedViewsContext = useContext(ArcViewContext);
    const { onViewMount, onViewUnmount } = mountedViewsContext ?? {};
    useEffect(() => {
      if (!mapContainer.current)
        return;
      mapView.container = mapContainer.current;
      mapView.when(() => {
        onViewCreated?.(mapView);
      });
      onViewMount?.(mapView, id);
      return () => {
        mapView.container = void 0;
        onViewUnmount?.(id);
      };
    }, [mapView, onViewCreated, id, onViewMount, onViewUnmount]);
    useEventHandlers(mapView, eventHandlers);
    return /* @__PURE__ */ jsxs(MapContext.Provider, { value: mapView, children: [
      /* @__PURE__ */ jsx("div", { ref: mapContainer, id, style, className, children: mapView && children }),
      Object.entries(mapViewProps).map(([key, value]) => {
        if (key === "map")
          return null;
        return /* @__PURE__ */ jsx(
          ArcReactiveProp,
          {
            accessor: mapView,
            property: key,
            value
          },
          key
        );
      })
    ] });
  };
  return memo(ArcView, isEqual);
}

export { MapContext, createViewComponent };
